import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaFolder, FaArrowLeft, FaShareAlt, FaFacebook, FaTwitter, FaYoutube, FaChevronLeft, FaChevronRight } from '../../utils/iconUtils';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { getNewsById } from '../../firebase/firestoreService';
import { cache } from '../../utils/cacheManager';
import CommentSection from '../../components/common/CommentSection';
import SeoHead from '../../components/seo/SeoHead';
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  getArticleDescription,
  toAbsoluteUrl,
} from '../../utils/seo';

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState<any[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
  const [viewerIndex, setViewerIndex] = useState<number>(0);
  // Manual tweak for carousel image vertical focus (0 = top, 50 = center, 100 = bottom)
  const carouselImagePositionY = 20;

  // Function to extract YouTube video ID from URL
  const extractYoutubeId = (url: string): string => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : '';
  };

  // Function to validate and format YouTube URL
  const getYoutubeEmbedUrl = (url: string): string => {
    const videoId = extractYoutubeId(url);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return '';
  };

  useEffect(() => {
    const fetchNews = async () => {
      if (!id) return;
      // Ensure the page is scrolled to top when loading a new article
      try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch (e) {}
      
      try {
        setLoading(true);

        // Fetch the news article (use cache)
        const result = await cache.get(`news_by_id_${id}`, async () => getNewsById(id), 2 * 60 * 60 * 1000);
        if (result.success && result.data) {
          let article = result.data;
          setNews(article);

          // Check if news has youtubeUrl and process it
          if (result.data.youtubeUrl) {
            const embedUrl = getYoutubeEmbedUrl(result.data.youtubeUrl);
            if (embedUrl) {
              setYoutubeUrl(embedUrl);
              setShowVideo(true);
            }
          }

          // Here you could fetch related news by category
          // const relatedResult = await getNews({ category: result.data.category, limitCount: 3 });
          // if (relatedResult.success) {
          //   setRelatedNews(relatedResult.data.filter((item: any) => item.id !== id));
          // }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [id]);

    useEffect(() => {
      setCurrentImageIndex(0);
    }, [news?.imageUrl, news?.otherImages?.length]);

    // Keyboard navigation for the lightbox viewer
    useEffect(() => {
      if (!isViewerOpen) return;
      const images = [news?.imageUrl, ...(news?.otherImages || [])].filter(Boolean);
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsViewerOpen(false);
        if (e.key === 'ArrowRight') setViewerIndex(i => (i + 1) % images.length);
        if (e.key === 'ArrowLeft') setViewerIndex(i => (i - 1 + images.length) % images.length);
      };
      document.addEventListener('keydown', handler);
      // Prevent body scroll while viewer open
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handler);
        document.body.style.overflow = prev;
      };
    }, [isViewerOpen, news?.imageUrl, news?.otherImages]);

  // Auto-swipe carousel every 7 seconds when multiple images exist
  useEffect(() => {
    const imgs = [news?.imageUrl, ...(news?.otherImages || [])].filter(Boolean);
    if (imgs.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(i => (i + 1) % imgs.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [news?.imageUrl, news?.otherImages?.length]);

  const shareNews = (platform: string) => {
    const url = window.location.href;
    const title = news?.title || '';
    const text = news?.excerpt || '';
    
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'instagram':
        // Instagram does not provide a direct web-share URL; open Instagram homepage (users can share via app)
        shareUrl = `https://www.instagram.com/`;
        break;
      case 'whatsapp':
        // Use wa.me or api.whatsapp.com; include title + url for context
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      case 'youtube':
        if (news?.youtubeUrl) {
          shareUrl = news.youtubeUrl;
        }
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

    if (!news) {
    return (
      <>
        <SeoHead
          title={`Activity Not Found | ${SITE_NAME}`}
          description="The requested activity could not be found."
          canonicalUrl={id ? `${SITE_URL}/activities/${id}` : `${SITE_URL}/activities`}
          noIndex
        />
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Activity Not Found</h1>
          <p className="text-gray-600 mb-8">The activity you're looking for doesn't exist.</p>
          <Link to="/" className="btn-primary">
            <FaArrowLeft className="inline mr-2" />
            Back to Home
          </Link>
        </div>
      </>
    );
  }


  const images = [news.imageUrl, ...(news.otherImages || [])].filter(Boolean);
  const canonicalUrl = `${SITE_URL}/activities/${news.id || id}`;
  const seoDescription = getArticleDescription(news.excerpt, news.content);
  const seoImage = toAbsoluteUrl(news.imageUrl || news.otherImages?.[0]) || DEFAULT_OG_IMAGE;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: canonicalUrl,
    headline: news.title,
    description: seoDescription,
    image: images.length > 0 ? images.map((src: string) => toAbsoluteUrl(src)).filter(Boolean) : [seoImage],
    datePublished: news.date,
    dateModified: news.updatedAt || news.createdAt || news.date,
    articleSection: news.category || 'Activities',
    author: {
      '@type': 'Organization',
      name: news.author || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo512.png`,
      },
    },
  };

  return (
    <>
      <SeoHead
        title={`${news.title} | ${SITE_NAME}`}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        imageUrl={seoImage}
        type="article"
        publishedTime={news.date}
        modifiedTime={news.updatedAt || news.createdAt || news.date}
        section={news.category}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header Image / Gallery Carousel */}
      <div
        className="relative h-[24rem] md:h-[32rem] overflow-hidden"
        onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
        onTouchMove={(e) => setTouchEndX(e.touches[0].clientX)}
        onTouchEnd={() => {
          if (touchStartX !== null && touchEndX !== null) {
            const delta = touchEndX - touchStartX;
            if (delta > 50) {
              // swipe right -> previous
              setCurrentImageIndex(i => Math.max(0, i - 1));
            } else if (delta < -50) {
              // swipe left -> next
              setCurrentImageIndex(i => (images.length ? (i + 1) % images.length : 0));
            }
          }
          setTouchStartX(null);
          setTouchEndX(null);
        }}
      >
        {/* render images from combined images array */}
        {images.map((src: string, idx: number) => (
          <img
            key={src + idx}
            src={src}
            alt={`${news.title}-${idx}`}
            onClick={() => {
              setViewerIndex(idx);
              setIsViewerOpen(true);
            }}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'} cursor-pointer`}
            style={{ objectPosition: `center ${carouselImagePositionY}%` }}
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>

        {/* View Images Button - Top Right */}
        {images.length > 0 && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <div className="flex items-center gap-2 bg-black/50 text-white px-3 py-2 rounded-lg border border-white/20 shadow-lg backdrop-blur-sm animate-pulse">
              {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M2 12a1 1 0 0 1 1-1h12.586l-3.293-3.293a1 1 0 1 1 1.414-1.414l5.707 5.707a1 1 0 0 1 0 1.414l-5.707 5.707a1 1 0 0 1-1.414-1.414L15.586 13H3a1 1 0 0 1-1-1z" />
              </svg> */}
              <span className="text-xs font-semibold tracking-wide">Click to view images 👉</span>
            </div>
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setViewerIndex(currentImageIndex);
                setIsViewerOpen(true);
              }}
              className="bg-black/60 hover:bg-black/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 backdrop-blur-sm border border-white/20 shadow-lg"
              aria-label="View all images"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                />
              </svg>
              <span className="text-sm font-medium">
                View Images {images.length > 1 && `(${images.length})`}
              </span>
            </button>
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-between px-4 z-20 pointer-events-none">
          {images.length > 1 && (
            <button
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(i => (i - 1 + images.length) % images.length);
              }}
              className="bg-black/40 text-white p-2 rounded-full hover:bg-black/50 pointer-events-auto"
              aria-label="Previous image"
            >
              <FaChevronLeft />
            </button>
          )}
          <div />
          {images.length > 1 && (
            <button
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(i => (i + 1) % images.length);
              }}
              className="bg-black/40 text-white p-2 rounded-full hover:bg-black/50 pointer-events-auto"
              aria-label="Next image"
            >
              <FaChevronRight />
            </button>
          )}
        </div>

        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <Link to="/" className="inline-flex items-center text-white hover:text-primary-200 mb-4">
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{news.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90">
              <span className="flex items-center">
                <FaCalendarAlt className="mr-2" />
                {new Date(news.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              {news.author && (
                <span className="flex items-center">
                  <FaUser className="mr-2" />
                  <span className="text-sm">Published by {news.author}</span>
                </span>
              )}
              <span className="flex items-center">
                <FaFolder className="mr-2" />
                {news.category}
              </span>
              {news.youtubeUrl && (
                <span className="flex items-center">
                  <FaYoutube className="mr-2 text-red-500" />
                  <span className="text-white/90">Video Available</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Viewer Modal */}
      {isViewerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <button
            className="absolute top-6 right-6 text-white text-2xl p-2 rounded-full bg-black/30"
            aria-label="Close viewer"
            onClick={() => setIsViewerOpen(false)}
          >
            ✕
          </button>

          <div className="relative max-w-6xl w-full mx-4">
            <div className="relative h-[70vh] md:h-[80vh] flex items-center justify-center">
              <button
                className="absolute left-2 md:left-6 z-20 text-white bg-black/30 p-3 rounded-full"
                onClick={() => setViewerIndex(i => (i - 1 + images.length) % images.length)}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>

              <img
                src={images[viewerIndex]}
                alt={`viewer-${viewerIndex}`}
                className="max-h-[70vh] md:max-h-[80vh] w-auto max-w-full object-contain mx-auto"
              />

              <button
                className="absolute right-2 md:right-6 z-20 text-white bg-black/30 p-3 rounded-full"
                onClick={() => setViewerIndex(i => (i + 1) % images.length)}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2 overflow-x-auto">
              {images.map((src: string, idx: number) => (
                <button
                  key={src + idx + '-thumb'}
                  onClick={() => setViewerIndex(idx)}
                  className={`w-16 h-12 flex-shrink-0 overflow-hidden rounded ${idx === viewerIndex ? 'ring-2 ring-white' : ''}`}
                >
                  <img src={src} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left column: YouTube, stats, related */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {/* YouTube Video Embed */}
              {showVideo && youtubeUrl && (
                <div className="mb-0">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      <FaYoutube className="mr-2 text-red-500" />
                      Watch Video
                    </h3>
                    <button
                      onClick={() => setShowVideo(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Hide Video
                    </button>
                  </div>
                  <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-lg">
                    <iframe
                      src={youtubeUrl}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute top-0 left-0 w-full h-full"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Click the play button to watch the video. You can also 
                    <a 
                      href={news.youtubeUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 ml-1"
                    >
                      watch it directly on YouTube
                    </a>.
                  </p>
                </div>
              )}

              {/* Show YouTube button if video exists but is hidden */}
              {!showVideo && news.youtubeUrl && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FaYoutube className="text-red-500 text-2xl mr-3" />
                      <div>
                        <h4 className="font-bold text-gray-900">Video Content Available</h4>
                        <p className="text-sm text-gray-600">This article includes a related video</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowVideo(true)}
                      className="btn-primary flex items-center"
                    >
                      <FaYoutube className="mr-2" />
                      Watch Video
                    </button>
                  </div>
                </div>
              )}

              {/* Statistics */}
              <div className="bg-primary-700 text-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl text-gray-100 font-bold mb-4">Article Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Published</span>
                    <span className="font-bold">
                      {new Date(news.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Category</span>
                    <span className="font-bold">{news.category}</span>
                  </div>
                  {news.youtubeUrl && (
                    <div className="flex justify-between items-center">
                      <span>Video</span>
                      <span className="font-bold flex items-center">
                        <FaYoutube className="mr-1 text-red-300" />
                        Available
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* YouTube Quick Link */}
              {news.youtubeUrl && (
                <div className="bg-white rounded-xl shadow-lg p-6 mt-2 border border-red-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <FaYoutube className="mr-2 text-red-500" />
                    Video Content
                  </h3>
                  <p className="text-gray-600 mb-4">
                    This article has an accompanying video that provides additional context and insights.
                  </p>
                  <a
                    href={news.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-red w-full flex items-center justify-center"
                  >
                    <FaYoutube className="mr-2" />
                    Watch on YouTube
                  </a>
                </div>
              )}

              {/* Related News */}
              {relatedNews.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Related News</h3>
                  <div className="space-y-4">
                    {relatedNews.map((item) => (
                      <Link
                          key={item.id}
                          to={`/activities/${item.id}`}
                          className="block group"
                        >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-16 h-16">
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-full h-full object-cover rounded"
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 group-hover:text-primary-700 transition-colors line-clamp-2">
                              {item.title}
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {new Date(item.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                            {item.youtubeUrl && (
                              <div className="flex items-center mt-1">
                                <FaYoutube className="text-red-500 text-xs mr-1" />
                                <span className="text-xs text-gray-500">Video</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: Main content */}
          <motion.div 
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
              <h2 className="text-2xl font-extrabold text-gray-900 mb-4 ml-2">{news.title}</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              {/* Tags */}
              {news.tags && news.tags.length > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {news.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Content */}
              <div 
                className="prose prose-lg max-w-none mb-8"
                dangerouslySetInnerHTML={{ __html: news.content }}
              />

              {/* Share Buttons */}
              <div className="border-t border-gray-200 pt-8 mt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FaShareAlt className="text-gray-500" />
                    <span className="text-gray-700 font-medium">Share:</span>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => shareNews('facebook')}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
                      aria-label="Share on Facebook"
                    >
                      <FaFacebook />
                    </button>
                    <button
                      onClick={() => shareNews('twitter')}
                      className="w-10 h-10 rounded-full bg-blue-400 text-white flex items-center justify-center hover:bg-blue-500 transition-colors"
                      aria-label="Share on Twitter"
                    >
                      <FaTwitter />
                    </button>
                    <button
                      onClick={() => shareNews('instagram')}
                      className="w-10 h-10 rounded-full bg-gradient-to-tr from-pink-500 via-indigo-500 to-yellow-400 text-white flex items-center justify-center hover:opacity-95 transition-colors"
                      aria-label="Share on Instagram"
                    >
                      <FaInstagram />
                    </button>
                    <button
                      onClick={() => shareNews('whatsapp')}
                      className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-colors"
                      aria-label="Share on WhatsApp"
                    >
                      <FaWhatsapp />
                    </button>
                    {news.youtubeUrl && (
                      <button
                        onClick={() => shareNews('youtube')}
                        className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors"
                        aria-label="Watch on YouTube"
                      >
                        <FaYoutube />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Add Comment Section here */}
<div className="mt-12">
  <CommentSection newsId={id!} />
</div>
            </div>
          </motion.div>
        </div>
      </div>
      </div>
    </>
  );
};

export default NewsDetail;
